import { TaskModel } from '@bryntum/gantt';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import {
  formatDate,
  GanttType,
  getDueDateForGantt,
  getDueDateFromGantt,
  getGanttPhaseId,
  getGanttTaskId,
} from 'components/organisms/projects/project-plan/timeline/helpers';
import {
  ProjectTemplateData,
  useGetAllProjectPhases,
  useGetAllTasks,
} from 'data/templates/projects';
import {
  addPhaseDependencyInProjectTemplate,
  addTaskDependencyInProjectTemplate,
  updatePhaseInProjectTemplate,
  updateTaskInProjectTemplate,
} from 'data/templates/projects/actions';
import {
  updateProjectTemplateProjectPhases,
  updateProjectTemplateTasks,
} from 'data/templates/projects/slices';
import { useTranslate } from 'hooks/use-translation';
import {
  addDays,
  DateShiftUpdates,
  EntityType,
  getDateShiftsForEntity,
  List,
} from 'utils/date-shift';
import {
  getPhaseList,
  getTaskList,
  isValidDateShift,
} from 'utils/template-date-shift';

type CreateDependencyEventPayload = {
  source: TaskModel;
  target: TaskModel;
};

type GanttEvent = {
  name: string;
  payload: TaskModel | CreateDependencyEventPayload;
};

type TemplateDataUpdate = Required<Pick<ProjectTemplateData, 'id'>> &
  Partial<Pick<ProjectTemplateData, 'duration' | 'startDay'>>;

const getTemplateDataUpdates = (
  dateShiftUpdates: DateShiftUpdates,
  list: List,
): TemplateDataUpdate[] => {
  const dataUpdates: TemplateDataUpdate[] = [];

  Object.values(dateShiftUpdates).forEach(({ id, startDate, dueDate }) => {
    const update: TemplateDataUpdate = { id };
    const currentEntity = list[id];

    if (startDate) {
      update.startDay = Math.abs(
        moment().startOf('day').diff(moment(startDate), 'day'),
      );
    }
    if (startDate || dueDate) {
      const _startDate = startDate || currentEntity.startDate;

      update.duration = Math.round(
        moment(dueDate || currentEntity.dueDate)
          .endOf('day')
          .diff(moment(_startDate).startOf('day'), 'days', true),
      );
    }
    dataUpdates.push(update);
  });

  return dataUpdates;
};

export const useTemplateGanttEventsConfig = ({ ganttRef }) => {
  /**
   * Component state used to trigger event handlers
   * since Gantt is caching the event handler
   * due to which data models are cached resulting in unexpected behaviour.
   */
  const [event, setEvent] = useState(null as unknown as GanttEvent);
  const dispatch = useDispatch();
  const projectPhases = useGetAllProjectPhases();
  const tasks = useGetAllTasks();

  const translate = useTranslate();

  const updateGanttDates = useCallback(
    (ganttId, currentEntity, startDate?, dueDate?) => {
      const { taskStore, project } = ganttRef?.instance;
      const record = taskStore.getById(ganttId);

      if (!record) {
        return;
      }

      if (startDate) {
        if (moment(startDate).isBefore(moment(project.startDate))) {
          project.setStartDate(startDate);
        }
      }
      record.setStartDate(formatDate(startDate || currentEntity.startDate));
      record.setEndDate(getDueDateForGantt(dueDate || currentEntity.dueDate));
    },
    [ganttRef],
  );

  const updateGanttData = useCallback(
    (ganttId, startDay, duration) => {
      const { taskStore } = ganttRef?.instance;
      const record = taskStore.getById(ganttId);

      if (!record) {
        return;
      }
      if (startDay) {
        record.set('startDay', startDay);
      }
      if (duration) {
        record.set('actualDuration', duration);
      }
    },
    [ganttRef],
  );

  const onDrop = useCallback(
    (entity) => {
      const { _id: entityId, type: entityType } = entity;
      const phaseList = getPhaseList(Object.values(projectPhases));
      const taskList = getTaskList(Object.values(tasks));
      const isTask =
        entityType === GanttType.TaskBar ||
        entityType === GanttType.MilestoneBar;

      const currentEntity = isTask ? taskList[entityId] : phaseList[entityId];

      const newStartDate = moment(entity.get('startDate'));
      const newDueDate = addDays(newStartDate, currentEntity.duration, true);

      const [phaseUpdatesList, taskUpdatesList] = getDateShiftsForEntity(
        phaseList,
        taskList,
        {
          id: entityId,
          startDate: formatDate(newStartDate),
          dueDate: formatDate(newDueDate),
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: currentEntity.depth,
        },
        {
          allowWeekend: true,
          alignParentWithChildren: true,
        },
      );

      const isValid = isValidDateShift(phaseUpdatesList);

      if (!isValid) {
        if (isTask) {
          updateGanttDates(getGanttTaskId(entityId), taskList[entityId]);
        } else {
          updateGanttDates(getGanttPhaseId(entityId), phaseList[entityId]);
        }

        toastService({
          title: translate('failure'),
          message: translate('templates.date.shift.start.day.error'),
        }).error();

        return;
      }
      const phaseUpdates = Object.values(phaseUpdatesList);
      const taskUpdates = Object.values(taskUpdatesList);

      phaseUpdates?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        updateGanttDates(ganttId, phaseList[id], startDate, dueDate);
      });
      taskUpdates?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        updateGanttDates(ganttId, taskList[id], startDate, dueDate);
      });

      if (taskUpdates.length > 0) {
        const dataUpdates = getTemplateDataUpdates(taskUpdates, taskList);

        dataUpdates?.forEach(({ startDay, duration, id }) => {
          const ganttId = getGanttTaskId(id);

          updateGanttData(ganttId, startDay, duration);
        });

        dispatch(updateProjectTemplateTasks(dataUpdates));
      }

      if (phaseUpdates.length > 0) {
        const dataUpdates = getTemplateDataUpdates(phaseUpdates, phaseList);

        dataUpdates?.forEach(({ startDay, duration, id }) => {
          const ganttId = getGanttPhaseId(id);

          updateGanttData(ganttId, startDay, duration);
        });

        dispatch(updateProjectTemplateProjectPhases(dataUpdates));
      }
    },
    [
      dispatch,
      projectPhases,
      tasks,
      translate,
      updateGanttData,
      updateGanttDates,
    ],
  );

  /**
   * only tasks can be resized and NOT the phases
   * (as per our implementation, not limitation from Bryntum)
   * hence the following handler works assuming task is resized.
   */
  const onResize = useCallback(
    (entity) => {
      const { _id: entityId, type: entityType } = entity;
      const phaseList = getPhaseList(Object.values(projectPhases));
      const taskList = getTaskList(Object.values(tasks));
      const isTask =
        entityType === GanttType.TaskBar ||
        entityType === GanttType.MilestoneBar;
      const newDueDate = getDueDateFromGantt(entity);

      const [phaseUpdatesList, taskUpdatesList] = getDateShiftsForEntity(
        phaseList,
        taskList,
        {
          id: entityId,
          dueDate: formatDate(newDueDate),
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: taskList[entityId].depth,
        },
        {
          allowWeekend: true,
          alignParentWithChildren: true,
        },
      );
      const phaseUpdates = Object.values(phaseUpdatesList);
      const taskUpdates = Object.values(taskUpdatesList);

      phaseUpdates?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        updateGanttDates(ganttId, phaseList[id], startDate, dueDate);
      });
      taskUpdates?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        updateGanttDates(ganttId, taskList[id], startDate, dueDate);
      });

      if (taskUpdates.length > 0) {
        const dataUpdates = getTemplateDataUpdates(taskUpdates, taskList);

        dataUpdates?.forEach(({ startDay, duration, id }) => {
          const ganttId = getGanttTaskId(id);

          updateGanttData(ganttId, startDay, duration);
        });
        dispatch(updateProjectTemplateTasks(dataUpdates));
      }

      if (phaseUpdates.length > 0) {
        const dataUpdates = getTemplateDataUpdates(phaseUpdates, phaseList);

        dataUpdates?.forEach(({ startDay, duration, id }) => {
          const ganttId = getGanttPhaseId(id);

          updateGanttData(ganttId, startDay, duration);
        });
        dispatch(updateProjectTemplateProjectPhases(dataUpdates));
      }
    },
    [dispatch, projectPhases, tasks, updateGanttData, updateGanttDates],
  );

  const onDependencyCreate = useCallback(
    ({ source, target }) => {
      const phaseList = getPhaseList(Object.values(projectPhases));
      const taskList = getTaskList(Object.values(tasks));
      const { _id: targetEntityId } = target;
      const { _id: sourceEntityId, type: sourceEntityType } = source;
      const isTask =
        sourceEntityType === GanttType.TaskBar ||
        sourceEntityType === GanttType.MilestoneBar;

      const sourceEntity = isTask
        ? taskList[sourceEntityId]
        : phaseList[sourceEntityId];

      const [phaseUpdatesList, taskUpdatesList] = getDateShiftsForEntity(
        phaseList,
        taskList,
        {
          id: sourceEntityId,
          dependencyFor: [
            ...(sourceEntity.dependencyFor || []),
            targetEntityId,
          ],
          type: isTask ? EntityType.Task : EntityType.Phase,
          depth: sourceEntity.depth,
        },
        {
          allowWeekend: true,
          alignParentWithChildren: true,
        },
      );
      const phaseUpdates = Object.values(phaseUpdatesList);
      const taskUpdates = Object.values(taskUpdatesList);

      phaseUpdates?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttPhaseId(id);

        updateGanttDates(ganttId, phaseList[id], startDate, dueDate);
      });
      taskUpdates?.forEach(({ startDate, dueDate, id }) => {
        const ganttId = getGanttTaskId(id);

        updateGanttDates(ganttId, taskList[id], startDate, dueDate);
      });

      if (isTask) {
        dispatch(
          addTaskDependencyInProjectTemplate({
            taskId: targetEntityId,
            dependentTaskId: sourceEntityId,
          }),
        );
      } else {
        dispatch(
          addPhaseDependencyInProjectTemplate({
            phaseId: targetEntityId,
            dependentPhaseId: sourceEntityId,
          }),
        );
      }
      if (taskUpdates.length > 0) {
        const dataUpdates = getTemplateDataUpdates(taskUpdates, taskList);

        dataUpdates?.forEach(({ startDay, duration, id }) => {
          const ganttId = getGanttTaskId(id);

          updateGanttData(ganttId, startDay, duration);
        });

        dispatch(updateProjectTemplateTasks(dataUpdates));
      }

      if (phaseUpdates.length > 0) {
        const dataUpdates = getTemplateDataUpdates(phaseUpdates, phaseList);

        dataUpdates?.forEach(({ startDay, duration, id }) => {
          const ganttId = getGanttPhaseId(id);

          updateGanttData(ganttId, startDay, duration);
        });

        dispatch(updateProjectTemplateProjectPhases(dataUpdates));
      }
    },
    [dispatch, projectPhases, tasks, updateGanttData, updateGanttDates],
  );

  const onDependencyDelete = useCallback(
    async ({ source, target }: { source: TaskModel; target: TaskModel }) => {
      const targetEntity = target.get('data');
      const sourceEntityType = source.get('type');
      const isTask =
        sourceEntityType === GanttType.TaskBar ||
        sourceEntityType === GanttType.MilestoneBar;

      if (isTask) {
        const { taskId: sourceEntityId } = source.get('data');

        dispatch(
          updateTaskInProjectTemplate({
            taskId: targetEntity.taskId,
            payload: {
              ...targetEntity,
              dependentTasks: (targetEntity.dependentTasks || []).filter(
                (t) => t.taskId !== sourceEntityId,
              ),
            },
          }),
        );
      } else {
        const { projectPhaseId: sourceEntityId } = source.get('data');

        dispatch(
          updatePhaseInProjectTemplate({
            projectPhaseId: targetEntity.projectPhaseId,
            payload: {
              dependentProjectPhases: (
                targetEntity.dependentProjectPhases || []
              ).filter((t) => t.projectPhaseId !== sourceEntityId),
            },
          }),
        );
      }
    },
    [dispatch],
  );

  useEffect(() => {
    switch (event?.name) {
      case 'onDrop':
        onDrop(event.payload as TaskModel);
        break;
      case 'onResize':
        onResize(event.payload as TaskModel);
        break;
      case 'onDependencyCreate':
        onDependencyCreate(event.payload as CreateDependencyEventPayload);
        break;
      case 'onDependencyDelete':
        onDependencyDelete(event.payload as CreateDependencyEventPayload);
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  return useMemo(
    () => ({
      listeners: {
        taskDrop: ({ taskRecords }: { taskRecords: TaskModel[] }): void => {
          setEvent({
            name: 'onDrop',
            payload: taskRecords[0],
          });
        },
        taskResizeEnd: ({ taskRecord }: { taskRecord: TaskModel }): void => {
          setEvent({
            name: 'onResize',
            payload: taskRecord,
          });
        },
        afterDependencyCreateDrop: ({
          data: { source, target, valid },
        }: {
          data: {
            source: TaskModel;
            target: TaskModel;
            valid: boolean;
          };
        }) => {
          if (!valid) {
            return;
          }
          setEvent({
            name: 'onDependencyCreate',
            payload: { source, target },
          });
        },
        dependencyClick: ({ dependency }) => {
          confirm({
            title: translate('timeline.dependency.delete.confirm.title'),
            content: translate('timeline.dependency.delete.confirm.content', {
              from: dependency.fromEvent.name,
              to: dependency.toEvent.name,
              b: (chunks) => <b>{chunks}</b>,
            }),
            onOk: () => {
              dependency.remove();
              setEvent({
                name: 'onDependencyDelete',
                payload: {
                  source: dependency.fromEvent,
                  target: dependency.toEvent,
                },
              });
            },
          });
        },
      },
    }),
    [translate],
  );
};
